/**
 * The bordered "headline + enrollment CTA" bar, shared by the standalone CTA band
 * and the AI Labs block. Owns the CTA resolution and its click tracking so the
 * two surfaces can't drift apart again.
 *
 * Resolves the CTA through `useEnrollmentCtaConfig` rather than
 * `useLandingEnrollment`: the latter only forwards the SSR cohort seed to the
 * *banner* hook, so it added a redundant subscription for no CTA benefit.
 */
import Link from 'next/link'
import { useEnrollmentCtaConfig } from '@educator/hooks/useEnrollmentCtaConfig'
import { useFireEvent } from '@educator/tracking/educatorTracking'

interface InlineCtaProps {
  /** Translated headline. Shown at every breakpoint unless `mobileHeadline` is set. */
  headline: string
  /** Translated short headline swapped in below `md`. */
  mobileHeadline?: string
  /**
   * `cta_location` / `location` sent with the click events. Narrowed to the
   * surfaces that exist today — the tracking module keeps its location enums
   * private, so widening this is a deliberate edit rather than a silent `string`.
   */
  ctaLocation: 'ai labs' | 'cta band'
}

export const InlineCta = ({
  headline,
  mobileHeadline,
  ctaLocation,
}: InlineCtaProps) => {
  const fireEvent = useFireEvent()
  const { primaryCta, isPrimaryLogIn } = useEnrollmentCtaConfig()

  const handleClick = () => {
    if (!isPrimaryLogIn) {
      fireEvent('educatorPurchaseClick', {
        cta_location: ctaLocation,
        cta: primaryCta.label,
      })
    }
    fireEvent('educatorSignClick', {
      action: primaryCta.label.toLowerCase(),
      location: ctaLocation,
    })
  }

  return (
    <div className='flex items-center gap-3 border border-edu-neutral-1/20 p-4 md:p-6'>
      <p className='m-0 flex-1 text-body-3 leading-body-3 text-edu-fg-primary md:text-title-3 md:leading-title-3'>
        {mobileHeadline ? (
          <>
            <span className='md:hidden'>{mobileHeadline}</span>
            <span className='hidden md:inline'>{headline}</span>
          </>
        ) : (
          headline
        )}
      </p>
      <Link
        href={primaryCta.href}
        onClick={handleClick}
        className='shrink-0 bg-edu-brand-orange px-3 py-3 text-body-3 leading-body-3 font-semibold tracking-[0.7px] text-edu-fg-primary uppercase transition-colors duration-200 ease-out hover:bg-edu-orange-600 md:px-6 md:py-4 md:text-body-2 md:tracking-[0.8px]'
      >
        {primaryCta.label}
      </Link>
    </div>
  )
}
